import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Language, t } from '../data/translations';
import {
    GameNotification,
    getNotifications,
    getUnreadCount,
    markAllRead,
    clearAll,
} from '../services/notificationService';

interface NotificationBellProps {
    username: string;
    language: Language;
    /** External trigger to refresh notification list (increment to force re-read) */
    refreshTick?: number;
}

const TYPE_ICONS: Record<string, string> = {
    global: '🌐',
    faction: '⚔️',
    individual: '📩',
};

function timeAgo(timestamp: number, lang: Language): string {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return lang === 'ru' ? 'только что' : lang === 'uk' ? 'щойно' : lang === 'cn' ? '刚刚' : 'just now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
    username,
    language,
    refreshTick = 0,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<GameNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const panelRef = useRef<HTMLDivElement>(null);
    const bellRef = useRef<HTMLButtonElement>(null);

    // Refresh notifications from storage
    const refresh = useCallback(() => {
        if (!username) return;
        setNotifications(getNotifications(username).slice().reverse());
        setUnreadCount(getUnreadCount(username));
    }, [username]);

    useEffect(() => { refresh(); }, [refresh, refreshTick]);

    // Poll for changes every 5 seconds (lightweight localStorage read)
    useEffect(() => {
        const interval = setInterval(refresh, 5000);
        return () => clearInterval(interval);
    }, [refresh]);

    // Close on click outside
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e: MouseEvent) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target as Node) &&
                bellRef.current && !bellRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    const handleToggle = () => {
        setIsOpen(prev => !prev);
    };

    const handleMarkAllRead = () => {
        if (!username) return;
        markAllRead(username);
        refresh();
    };

    const handleClearAll = () => {
        if (!username) return;
        clearAll(username);
        refresh();
        setIsOpen(false);
    };

    return (
        <div className="relative" style={{ zIndex: 9999 }}>
            {/* Bell Button */}
            <button
                ref={bellRef}
                type="button"
                onClick={handleToggle}
                className="relative w-[46px] h-[46px] rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border shadow-lg cursor-pointer bg-black/40 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white hover:border-white/30"
                aria-label={t('notif.title', language)}
                style={{ pointerEvents: 'auto' }}
            >
                {/* Envelope SVG */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-5 h-5"
                >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 4 12 13 2 4" />
                </svg>

                {/* Unread badge */}
                {unreadCount > 0 && (
                    <span
                        className="absolute -top-1 -right-1 min-w-[20px] h-[20px] flex items-center justify-center rounded-full text-[11px] font-bold text-white px-1"
                        style={{
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            boxShadow: '0 0 8px rgba(239, 68, 68, 0.6)',
                            animation: 'pulse 2s infinite',
                        }}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Notification Panel */}
            {isOpen && (
                <div
                    ref={panelRef}
                    className="absolute right-0 mt-2 w-[340px] max-h-[420px] rounded-xl border shadow-2xl overflow-hidden"
                    style={{
                        background: 'rgba(15, 20, 40, 0.95)',
                        borderColor: 'rgba(99, 102, 241, 0.3)',
                        backdropFilter: 'blur(20px)',
                        animation: 'fadeSlideDown 0.2s ease-out',
                    }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                        <h3 className="text-sm font-bold text-white tracking-wide">
                            {t('notif.title', language)}
                        </h3>
                        <div className="flex gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllRead}
                                    className="text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors cursor-pointer"
                                >
                                    {t('notif.markAllRead', language)}
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    onClick={handleClearAll}
                                    className="text-[11px] text-red-400 hover:text-red-300 transition-colors cursor-pointer"
                                >
                                    {t('notif.clearAll', language)}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notification list */}
                    <div className="overflow-y-auto" style={{ maxHeight: '360px' }}>
                        {notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-slate-500">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2} className="w-10 h-10 mb-2 opacity-40">
                                    <rect x="2" y="4" width="20" height="16" rx="2" />
                                    <path d="M22 4 12 13 2 4" />
                                </svg>
                                <span className="text-sm">{t('notif.noNotifications', language)}</span>
                            </div>
                        ) : (
                            notifications.map((notif) => (
                                <div
                                    key={notif.id}
                                    className="px-4 py-3 border-b transition-colors hover:bg-white/5"
                                    style={{
                                        borderColor: 'rgba(255,255,255,0.04)',
                                        background: notif.read ? 'transparent' : 'rgba(99, 102, 241, 0.08)',
                                    }}
                                >
                                    <div className="flex items-start gap-2">
                                        <span className="text-base mt-0.5 flex-shrink-0">{TYPE_ICONS[notif.type] || '📌'}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span
                                                    className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                                    style={{
                                                        background: notif.type === 'global' ? 'rgba(59,130,246,0.2)' :
                                                            notif.type === 'faction' ? 'rgba(234,179,8,0.2)' :
                                                                'rgba(168,85,247,0.2)',
                                                        color: notif.type === 'global' ? '#93c5fd' :
                                                            notif.type === 'faction' ? '#fde047' :
                                                                '#c084fc',
                                                    }}
                                                >
                                                    {t(`notif.${notif.type}`, language)}
                                                </span>
                                                <span className="text-[10px] text-slate-500 ml-auto flex-shrink-0">
                                                    {timeAgo(notif.timestamp, language)}
                                                </span>
                                            </div>
                                            <p className="text-[13px] text-slate-200 leading-snug break-words">
                                                {t(notif.messageKey, language, notif.params)}
                                            </p>
                                        </div>
                                        {!notif.read && (
                                            <span className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0" style={{ boxShadow: '0 0 6px rgba(99,102,241,0.5)' }} />
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Inline animation keyframes */}
            <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
    );
};

export default NotificationBell;
